/**
 * FERP Portal - Personal Inbox Workflow Resolution Hook
 *
 * Resolves the current actor's personal inbox workflow instance.
 * Auto-creates instance on first use via getOrCreatePersonalInbox mutation.
 *
 * The personal inbox (wf-0-0-100) is auto-provisioned per actor for
 * quick task capture without navigating to a formal workflow.
 *
 * FERP Principles:
 * - Backend-driven: Uses getOrCreatePersonalInbox mutation
 * - Cached: localStorage for instant subsequent access
 */

import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  GetOrCreatePersonalInboxDocument,
  GetOrCreatePersonalInboxMutation,
} from '@/generated/graphql';

interface PersonalInboxInfo {
  workflowInstanceId: string | null;
  loading: boolean;
  error: Error | null;
  resolved: boolean;
}

const INBOX_CACHE_KEY = 'ferp:personal_inbox';

/**
 * Returns the current actor's personal inbox workflow instance ID.
 * Auto-provisions inbox on first use. Caches in localStorage for instant access.
 *
 * @param actorId - The current user's actor ID
 */
export function usePersonalInboxWorkflow(actorId: string | null): PersonalInboxInfo {
  const [inboxInfo, setInboxInfo] = useState<PersonalInboxInfo>({
    workflowInstanceId: null,
    loading: true,
    error: null,
    resolved: false,
  });

  const [getOrCreateInbox] = useMutation<GetOrCreatePersonalInboxMutation>(GetOrCreatePersonalInboxDocument);

  const resolveInbox = useCallback(async () => {
    if (!actorId) {
      setInboxInfo({
        workflowInstanceId: null,
        loading: false,
        error: null,
        resolved: false,
      });
      return;
    }

    // Check localStorage cache first
    const cacheKey = `${INBOX_CACHE_KEY}:${actorId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setInboxInfo({
        workflowInstanceId: cached,
        loading: false,
        error: null,
        resolved: true,
      });
      return;
    }

    // Resolve from backend
    setInboxInfo((prev) => ({ ...prev, loading: true }));

    try {
      const result = await getOrCreateInbox({
        variables: { actorId },
      });

      const instanceId = result.data?.instanceApiGetOrCreatePersonalInbox?.instanceId;

      if (instanceId) {
        // Cache for instant subsequent access
        localStorage.setItem(cacheKey, instanceId);

        setInboxInfo({
          workflowInstanceId: instanceId,
          loading: false,
          error: null,
          resolved: true,
        });
      } else {
        setInboxInfo({
          workflowInstanceId: null,
          loading: false,
          error: new Error('Failed to resolve personal inbox'),
          resolved: false,
        });
      }
    } catch (error) {
      setInboxInfo({
        workflowInstanceId: null,
        loading: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        resolved: false,
      });
    }
  }, [actorId, getOrCreateInbox]);

  useEffect(() => {
    resolveInbox();
  }, [resolveInbox]);

  return inboxInfo;
}

/**
 * Clears the cached personal inbox instance ID.
 * Useful if the inbox instance was deleted or needs re-provisioning.
 */
export function clearPersonalInboxCache(actorId: string): void {
  const cacheKey = `${INBOX_CACHE_KEY}:${actorId}`;
  localStorage.removeItem(cacheKey);
}
